import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { AlertCircle, Bug, CircleHelp, Paperclip, Send, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const HIDDEN_PATHS = new Set(['/auth/callback']);
const IMAGE_LIMIT = 10 * 1024 * 1024;
const VIDEO_LIMIT = 50 * 1024 * 1024;
const FEEDBACK_MEDIA_ACCEPT = 'image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm,video/quicktime';

const ALLOWED_MEDIA_RULES = {
  'image/jpeg': { kind: 'image', limit: IMAGE_LIMIT, extensions: ['.jpg', '.jpeg'] },
  'image/png': { kind: 'image', limit: IMAGE_LIMIT, extensions: ['.png'] },
  'image/webp': { kind: 'image', limit: IMAGE_LIMIT, extensions: ['.webp'] },
  'image/gif': { kind: 'image', limit: IMAGE_LIMIT, extensions: ['.gif'] },
  'video/mp4': { kind: 'video', limit: VIDEO_LIMIT, extensions: ['.mp4'] },
  'video/webm': { kind: 'video', limit: VIDEO_LIMIT, extensions: ['.webm'] },
  'video/quicktime': { kind: 'video', limit: VIDEO_LIMIT, extensions: ['.mov', '.qt'] },
};

function getOrCreateFeedbackSessionId() {
  try {
    const existing = sessionStorage.getItem('feedback_session_id');
    if (existing) {
      return existing;
    }
    const created = `feedback_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    sessionStorage.setItem('feedback_session_id', created);
    return created;
  } catch (error) {
    return `feedback_${Date.now()}`;
  }
}

function validateAttachment(file) {
  if (!file) {
    return null;
  }

  const mime = (file.type || '').toLowerCase();
  const rule = ALLOWED_MEDIA_RULES[mime];
  const dotIndex = file.name.lastIndexOf('.');
  const extension = dotIndex >= 0 ? file.name.slice(dotIndex).toLowerCase() : '';

  if (!rule) {
    return 'Attachment must be JPEG, PNG, WEBP, GIF, MP4, WEBM, or MOV.';
  }

  if (!rule.extensions.includes(extension)) {
    return `File extension does not match ${mime}.`;
  }

  if (rule.kind === 'image' && file.size > IMAGE_LIMIT) {
    return 'Images must be 10MB or smaller.';
  }

  if (rule.kind === 'video' && file.size > VIDEO_LIMIT) {
    return 'Videos must be 50MB or smaller.';
  }

  return null;
}

export default function FeedbackWidget() {
  const location = useLocation();
  const { user } = useAuth();

  const [open, setOpen] = useState(false);
  const [type, setType] = useState('bug');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [media, setMedia] = useState(null);
  const [mediaError, setMediaError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const apiUrl = process.env.REACT_APP_API_URL || '';
  const shouldHide = HIDDEN_PATHS.has(location.pathname);

  const sessionId = useMemo(() => getOrCreateFeedbackSessionId(), []);

  useEffect(() => {
    const openFromNavbar = () => {
      setOpen(true);
      setSubmitError('');
      setSubmitSuccess('');
    };

    window.addEventListener('lilycove:open-feedback', openFromNavbar);
    return () => {
      window.removeEventListener('lilycove:open-feedback', openFromNavbar);
    };
  }, []);

  if (shouldHide) {
    return null;
  }

  const resetForm = () => {
    setType('bug');
    setMessage('');
    setEmail('');
    setHoneypot('');
    setMedia(null);
    setMediaError('');
  };

  const closeModal = () => {
    setOpen(false);
    setSubmitError('');
    setSubmitSuccess('');
  };

  const onAttachmentChange = (event) => {
    const file = event.target.files && event.target.files[0] ? event.target.files[0] : null;
    const validationError = validateAttachment(file);

    if (validationError) {
      setMedia(null);
      setMediaError(validationError);
      return;
    }

    setMedia(file);
    setMediaError('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!message.trim()) {
      setSubmitError('Please provide a short description of the issue.');
      return;
    }

    if (mediaError) {
      setSubmitError(mediaError);
      return;
    }

    setSubmitting(true);
    setSubmitError('');
    setSubmitSuccess('');

    try {
      const context = {
        route_path: location.pathname,
        path: location.pathname,
        query: window.location.search,
        url: window.location.href,
        referrer: document.referrer || null,
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight,
        },
        dpr: window.devicePixelRatio || 1,
        language: navigator.language,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        online: navigator.onLine,
        user_agent: navigator.userAgent,
        platform: 'web',
        session_id: sessionId,
        user_id: user?.id || null,
        timestamp: new Date().toISOString(),
      };

      const formData = new FormData();
      formData.append('type', type);
      formData.append('message', message.trim());
      formData.append('honeypot', honeypot);
      formData.append('context_json', JSON.stringify(context));

      if (email.trim()) {
        formData.append('email', email.trim());
      }

      if (media) {
        formData.append('media', media);
      }

      const headers = {
        'X-Session-ID': sessionId,
      };

      if (user?.id) {
        headers['X-User-ID'] = user.id;
      }

      const response = await fetch(`${apiUrl}/feedback`, {
        method: 'POST',
        headers,
        body: formData,
      });

      const payload = await response.json().catch(() => null);

      if (!response.ok) {
        const detail = payload?.detail || payload?.error || 'Could not submit feedback right now.';
        throw new Error(detail);
      }

      resetForm();
      setSubmitSuccess('Feedback sent. Thank you for the report.');
    } catch (error) {
      setSubmitError(error.message || 'Could not submit feedback right now.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="fixed right-4 z-40 h-12 w-12 rounded-full bg-purple text-white shadow-xl shadow-purple/40 transition hover:bg-purple/90"
          style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 1rem)' }}
          aria-label="Give feedback"
          title="Give feedback"
        >
          <CircleHelp className="mx-auto h-5 w-5" />
        </button>
      )}

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 backdrop-blur-sm sm:items-center sm:p-4"
          onClick={closeModal}
        >
          <div
            className="w-full max-h-[92vh] overflow-y-auto rounded-t-2xl border border-purple/30 bg-[#12121b] p-5 sm:max-w-lg sm:rounded-xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-cream">Give Feedback</h2>
                <p className="text-sm text-cream/60">Report a bug or issue.</p>
              </div>
              <button
                type="button"
                onClick={closeModal}
                className="rounded-lg p-1 text-cream/60 transition hover:bg-white/10 hover:text-cream"
                aria-label="Close feedback form"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form className="space-y-4" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="feedback-type" className="mb-1 block text-sm text-cream/70">
                  Type
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setType('bug')}
                    className={`flex items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm transition ${
                      type === 'bug'
                        ? 'border-purple bg-purple/20 text-cream'
                        : 'border-purple/20 text-cream/70 hover:border-purple/40 hover:text-cream'
                    }`}
                  >
                    <Bug className="h-4 w-4" />
                    Bug
                  </button>
                  <button
                    type="button"
                    onClick={() => setType('issue')}
                    className={`flex items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm transition ${
                      type === 'issue'
                        ? 'border-purple bg-purple/20 text-cream'
                        : 'border-purple/20 text-cream/70 hover:border-purple/40 hover:text-cream'
                    }`}
                  >
                    <AlertCircle className="h-4 w-4" />
                    Issue
                  </button>
                </div>
              </div>

              <div>
                <label htmlFor="feedback-message" className="mb-1 block text-sm text-cream/70">
                  Message
                </label>
                <textarea
                  id="feedback-message"
                  rows={5}
                  value={message}
                  onChange={(event) => setMessage(event.target.value)}
                  placeholder="What happened?"
                  className="w-full resize-y rounded-lg border border-purple/20 bg-dark/80 px-3 py-2 text-sm text-cream placeholder:text-cream/30 focus:border-purple/50 focus:outline-none focus:ring-2 focus:ring-purple/40"
                />
              </div>

              <div>
                <label htmlFor="feedback-email" className="mb-1 block text-sm text-cream/70">
                  Email (optional)
                </label>
                <input
                  id="feedback-email"
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="you@example.com"
                  className="w-full rounded-lg border border-purple/20 bg-dark/80 px-3 py-2 text-sm text-cream placeholder:text-cream/30 focus:border-purple/50 focus:outline-none focus:ring-2 focus:ring-purple/40"
                />
              </div>

              <div>
                <label htmlFor="feedback-media" className="mb-1 block text-sm text-cream/70">
                  Attachment (optional)
                </label>
                <label
                  htmlFor="feedback-media"
                  className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-purple/40 bg-dark/70 px-3 py-2 text-sm text-cream/80 transition hover:border-purple/70"
                >
                  <Paperclip className="h-4 w-4" />
                  {media ? media.name : 'Attach image/video'}
                </label>
                <input
                  id="feedback-media"
                  type="file"
                  accept={FEEDBACK_MEDIA_ACCEPT}
                  onChange={onAttachmentChange}
                  className="hidden"
                />
                <p className="mt-1 text-xs text-cream/50">Images up to 10MB, videos up to 50MB.</p>
                {mediaError && <p className="mt-1 text-xs text-red-400">{mediaError}</p>}
              </div>

              <input
                type="text"
                autoComplete="off"
                tabIndex={-1}
                value={honeypot}
                onChange={(event) => setHoneypot(event.target.value)}
                className="hidden"
                aria-hidden="true"
              />

              {submitError && <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">{submitError}</div>}
              {submitSuccess && <div className="rounded-lg border border-green-500/30 bg-green-500/10 px-3 py-2 text-sm text-green-300">{submitSuccess}</div>}

              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={closeModal}
                  className="rounded-lg px-4 py-2 text-sm text-cream/70 transition hover:bg-white/5 hover:text-cream"
                >
                  Close
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex items-center gap-2 rounded-lg bg-purple px-4 py-2 text-sm font-medium text-white transition hover:bg-purple/90 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <Send className="h-4 w-4" />
                  {submitting ? 'Sending...' : 'Send'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
